import type { FormDataObjectRule, FormDataObject } from "../interfaces/formBuilder.interface"

type FormulaFunction = (args: number[]) => number

// Define available formula functions
const formulaFunctions: Record<string, FormulaFunction> = {
  SUM: (args: number[]) => args.reduce((sum, val) => sum + val, 0),
  AVG: (args: number[]) => (args.length ? args.reduce((sum, val) => sum + val, 0) / args.length : 0),
  MIN: (args: number[]) => Math.min(...args),
  MAX: (args: number[]) => Math.max(...args),
  ROUND: (args: number[]) => Math.round(args[0]),
  ABS: (args: number[]) => Math.abs(args[0]),
}

/**
 * Parses and evaluates a formula string
 * @param formula The formula string to evaluate
 * @param fieldMap Map of field IDs to field objects
 * @returns The result of the formula evaluation
 */
export const evaluateFormula = (formula: string, fieldMap: Map<string, FormDataObject>): number => {
  // First process any functions in the formula
  let processedFormula = processFunctions(formula, fieldMap)

  // Then replace field references with their values
  processedFormula = processedFormula.replace(/{([^}]+)}/g, (match, fieldId) => {
    const field = fieldMap.get(fieldId)
    if (!field) {
      console.warn(`Field with ID ${fieldId} not found in formula ${formula}`)
      return "0"
    }

    // Get numeric value from field
    const value = getNumericValue(field.value)
    return value.toString()
  })

  try {
    // Use Function constructor to safely evaluate the formula expression
    const result = new Function(`return ${processedFormula}`)()
    return typeof result === "number" ? result : 0
  } catch (error) {
    console.error(`Error evaluating formula: ${formula}`, error)
    return 0
  }
}

/**
 * Evaluates formulas in a form and updates field values accordingly
 * @param form The form data
 * @returns Updated form with calculated values
 */
export const evaluateFormulas = (form: FormDataObject[]): FormDataObject[] => {
  // Create a flat map of all fields for easy lookup
  const fieldMap = new Map<string, FormDataObject>()
  const fieldsWithFormulas: { field: FormDataObject; formula: string }[] = []

  // First pass: build field map and collect fields with formulas
  form.forEach((section) => {
    section.children.forEach((field) => {
      fieldMap.set(field.label, field)

      if (field.rules?.some((rule: FormDataObjectRule) => rule.behavior === "formula" && rule.formula)) {
        const formulaRule = field.rules.find((rule: FormDataObjectRule) => rule.behavior === "formula")
        if (formulaRule && formulaRule.formula) {
          fieldsWithFormulas.push({ field, formula: formulaRule.formula })
        }
      }
    })
  })

  // Second pass: evaluate formulas
  fieldsWithFormulas.forEach(({ field, formula }) => {
    const result = evaluateFormula(formula, fieldMap)
    // Update the field value with the calculated result
    field.value = result
  })

  return form
}

/**
 * Processes function calls in formulas
 * @param formula The formula string
 * @param fieldMap Map of field IDs to field objects
 * @returns Processed formula with function calls evaluated
 */
export const processFunctions = (formula: string, fieldMap: Map<string, FormDataObject>): string => {
  // Regular expression to match function calls
  // Example: SUM({field1}, {field2}, 10)
  const functionRegex = /(\w+)\(([^)]*)\)/g

  return formula.replace(functionRegex, (match, funcName, argsStr) => {
    const func = formulaFunctions[funcName.toUpperCase()]
    if (!func) {
      console.warn(`Unknown function in formula: ${funcName}`)
      return "0"
    }

    // Parse arguments
    const args = argsStr.split(",").map((arg: string) => {
      const trimmedArg = arg.trim()
      const fieldMatch = trimmedArg.match(/{([^}]+)}/)

      if (fieldMatch) {
        // If argument is a field reference, get its value
        const fieldId = fieldMatch[1]
        const field = fieldMap.get(fieldId)
        return field ? getNumericValue(field.value) : 0
      } else {
        // Otherwise, try to parse as a number
        return parseFloat(trimmedArg) || 0
      }
    })

    // Call the function with the arguments
    return func(args).toString()
  })
}

const getNumericValue = (value: any): number => {
  if (value === undefined || value === null || value === "") {
    return 0
  }

  const num = Number(value)
  return isNaN(num) ? 0 : num
}
